import { deepClone } from '@/components/dash/postCreator/utils.js'
import { TRACK_TYPES, CLIP_SOURCE_TYPES, DEFAULT_IMAGE_DURATION_MS } from './constants.js'

export const generateId = (prefix) => `${prefix}_${Math.random().toString(36).substr(2, 9)}`

export const createEmptyTimeline = ({ width = 1080, height = 1920, fps = 30 } = {}) => ({
    id: generateId('timeline'),
    version: 1,
    // aspectRatio/aspectRatioLabel are declared here (rather than left to resizeCanvasToRatio's own
    // first assignment) so Vue 2 can actually observe them - a property added by plain assignment on
    // an already-reactive object is invisible to any template/computed depending on it, which is the
    // same trap fillMode/backgroundMode each had to work around with $set.
    canvas: { width, height, fps, aspectRatio: width / height, aspectRatioLabel: 'custom', backgroundColor: '#000000', backgroundMode: 'blur' },
    // Which instant photo mode was last switched to/exported from (see MediaEditor.vue's
    // toggleDocumentMode/finish) - documentMode/currentMs are pure UI state, never persisted, so
    // re-editing a saved photo-from-video result had nothing to restore which frame it actually
    // was, always reopening on frame 0 regardless of what was really exported.
    photoFrameMs: 0,
    tracks: [
        { id: generateId('track'), type: TRACK_TYPES.MEDIA, clips: [] },
        { id: generateId('track'), type: TRACK_TYPES.OVERLAY, overlays: [] },
        { id: generateId('track'), type: TRACK_TYPES.AUDIO, clips: [] }
    ],
    exportSettings: {
        targetAspectRatio: '9:16',
        resolution: { width, height },
        fps,
        videoBitrate: 8000000,
        audioBitrate: 128000,
        container: 'mp4'
    }
})

export const createVideoClip = ({ fileId, sourceDurationMs, trimStartMs = 0, trimEndMs = null }) => {
    const resolvedTrimEnd = trimEndMs === null ? sourceDurationMs : trimEndMs
    return {
        id: generateId('clip'),
        sourceType: CLIP_SOURCE_TYPES.VIDEO,
        fileId,
        sourceDurationMs,
        sourceWidth: null,
        sourceHeight: null,
        trimStartMs,
        trimEndMs: resolvedTrimEnd,
        imageDurationMs: null,
        timelineStartMs: 0,
        timelineEndMs: resolvedTrimEnd - trimStartMs,
        playbackRate: 1,
        // Whether a non-1x playbackRate should keep the audio's original pitch (time-stretched) or
        // let it shift naturally with speed (simple resample) - see ClipToolPanel's "Vitesse" tool.
        preservePitch: true,
        muted: false,
        volume: 1,
        transform: {
            cropRect: { x: 0, y: 0, width: 1, height: 1 },
            // Remplir by default: picking a format is the first thing most edits do (MediaEditor.vue
            // opens on the crop tool for exactly that reason), and filling it edge to edge is what
            // people want far more often than letterboxing the whole media into it. Written out here
            // rather than by flipping the `fillMode || 'contain'` fallback every reader uses - a
            // draft saved before this key existed was authored under Ajuster, and changing what that
            // absence means would silently re-crop already-saved posts. Harmless at creation time:
            // the crop still starts as the full source against a canvas at the source's own ratio,
            // where cover and contain render identically.
            fillMode: 'cover',
            rotationDeg: 0,
            flipH: false,
            flipV: false
        },
        aspectRatioTarget: 'custom',
        segments: [{ startMs: 0, endMs: resolvedTrimEnd - trimStartMs, enabled: true }],
        silenceRanges: [],
        transitionIn: { type: 'none', durationMs: 0, easing: 'linear', easingPoints: [0, 0, 1, 1] },
        transitionOut: { type: 'none', durationMs: 0, easing: 'linear', easingPoints: [0, 0, 1, 1] }
    }
}

export const createImageClip = ({ fileId, imageDurationMs = DEFAULT_IMAGE_DURATION_MS }) => ({
    id: generateId('clip'),
    sourceType: CLIP_SOURCE_TYPES.IMAGE,
    fileId,
    sourceDurationMs: null,
    sourceWidth: null,
    sourceHeight: null,
    trimStartMs: 0,
    trimEndMs: imageDurationMs,
    imageDurationMs,
    timelineStartMs: 0,
    timelineEndMs: imageDurationMs,
    playbackRate: 1,
    muted: true,
    volume: 0,
    transform: {
        cropRect: { x: 0, y: 0, width: 1, height: 1 },
        // See createVideoClip for why the default lives here rather than in the readers' fallback.
        fillMode: 'cover',
        rotationDeg: 0,
        flipH: false,
        flipV: false
    },
    // 'custom', not '9:16': aspectRatioTarget is compared against ASPECT_RATIOS' *numeric* values
    // (ClipToolPanel.vue), so the string form matched nothing at all - an image opened the crop tool
    // with no format button highlighted, not even "Original".
    aspectRatioTarget: 'custom',
    segments: [{ startMs: 0, endMs: imageDurationMs, enabled: true }],
    silenceRanges: [],
    transitionIn: { type: 'none', durationMs: 0, easing: 'linear', easingPoints: [0, 0, 1, 1] },
    transitionOut: { type: 'none', durationMs: 0, easing: 'linear', easingPoints: [0, 0, 1, 1] }
})

export const createAudioClip = ({ fileId, sourceDurationMs, timelineStartMs = 0, timelineEndMs, volume = 0.8 }) => ({
    id: generateId('audioclip'),
    fileId,
    sourceDurationMs,
    trimStartMs: 0,
    trimEndMs: timelineEndMs !== undefined ? timelineEndMs - timelineStartMs : sourceDurationMs,
    timelineStartMs,
    timelineEndMs: timelineEndMs !== undefined ? timelineEndMs : sourceDurationMs,
    volume,
    fadeInMs: 500,
    fadeOutMs: 500,
    muted: false
})

// animationIn/animationOut/animationLinkMode: optional overrides for presets (see OverlayPalette.vue).
export const createOverlay = ({ type, widgetType = null, startMs = 0, endMs = 0, transform = {}, data = {}, animationIn = null, animationOut = null, animationLinkMode = 'continuity' }) => ({
    id: generateId('overlay'),
    type,
    widgetType,
    startMs,
    endMs,
    transform: {
        xPct: 0.5, yPct: 0.5, widthPct: 0.3, heightPct: 0.1, rotationDeg: 0, scale: 1, zIndex: 1,
        ...transform
    },
    // styles is a list (e.g. ['slide', 'fade']) - several can combine at once.
    animationIn: { styles: [], direction: 'left', durationMs: 0, ...animationIn },
    animationOut: { styles: [], direction: 'left', durationMs: 0, ...animationOut },
    // continuity | mirror | different - see overlayAnimation.js's resolveAnimationOut.
    animationLinkMode,
    // Overlays sharing the same groupId form a group (see ElementsTree.vue) - null means ungrouped.
    // A plain deepClone (duplicateOverlay) copies this verbatim, so an individual copy naturally
    // joins its source's group; duplicating a whole group instead overwrites it with a fresh id
    // (see MediaEditor.vue's onDuplicateOverlayGroup).
    groupId: null,
    data
})

// A copy of an existing overlay (own id, everything else identical) - the simple alternative to
// having one overlay's own properties change partway through the video: add a second one instead,
// then adjust the copy's own timing/position/content/color for whichever other moment it's meant
// for. Placed right after the original's own end (clamped to the timeline's own duration) so it
// doesn't silently sit exactly on top of the original, unselected and easy to miss.
// Nudged a few % off the original (not left exactly on top of it, xPct/yPct unchanged) so the
// copy actually reads as a second element rather than an invisible duplicate stacked on the first.
const DUPLICATE_OFFSET_PCT = 0.04

export const duplicateOverlay = (overlay, timelineDurationMs) => {
    const durationMs = overlay.endMs - overlay.startMs
    const startMs = Math.max(0, Math.min(overlay.endMs, Math.max(0, timelineDurationMs - durationMs)))
    const copy = {
        ...deepClone(overlay),
        id: generateId('overlay'),
        startMs,
        endMs: startMs + durationMs
    }
    copy.transform.xPct = Math.min(1, copy.transform.xPct + DUPLICATE_OFFSET_PCT)
    copy.transform.yPct = Math.min(1, copy.transform.yPct + DUPLICATE_OFFSET_PCT)
    return copy
}

// Clip copy/paste - resets transitions to none, since a straight copy would transition into/out of itself.
export const duplicateMediaClip = (clip) => {
    const copy = deepClone(clip)
    copy.id = generateId('clip')
    copy.transitionIn = { type: 'none', durationMs: 0, easing: 'linear', easingPoints: [0, 0, 1, 1] }
    copy.transitionOut = { type: 'none', durationMs: 0, easing: 'linear', easingPoints: [0, 0, 1, 1] }
    return copy
}

// Centered crop rect (normalized 0-1, in the source's own pixel space) that turns a
// sourceWidth x sourceHeight video into the given target aspect ratio (width/height).
export const computeCropRectForRatio = (sourceWidth, sourceHeight, targetRatio) => {
    const sourceRatio = sourceWidth / sourceHeight
    if (sourceRatio > targetRatio) {
        const cropWidth = sourceHeight * targetRatio
        const x = (sourceWidth - cropWidth) / 2 / sourceWidth
        return { x, y: 0, width: cropWidth / sourceWidth, height: 1 }
    }
    const cropHeight = sourceWidth / targetRatio
    const y = (sourceHeight - cropHeight) / 2 / sourceHeight
    return { x: 0, y, width: 1, height: cropHeight / sourceHeight }
}

export const getClipDuration = (clip) => clip.timelineEndMs - clip.timelineStartMs

export const getMediaTrack = (timeline) => timeline.tracks.find(t => t.type === TRACK_TYPES.MEDIA)
export const getOverlayTrack = (timeline) => timeline.tracks.find(t => t.type === TRACK_TYPES.OVERLAY)
export const getAudioTrack = (timeline) => timeline.tracks.find(t => t.type === TRACK_TYPES.AUDIO)

export const getEnabledSegments = (clip) => (clip.segments || []).filter(s => s.enabled)

// Duration of a clip once its disabled (deleted) internal segments are removed, adjusted for
// playback speed - a 10s clip at 2x plays back (and so occupies) 5s of the shared timeline, at
// 0.5x it occupies 20s. Segment math itself stays in *source* time (untouched by speed); only
// the final duration exposed to the rest of the timeline is scaled.
export const getEffectiveClipDuration = (clip) => {
    const segments = getEnabledSegments(clip)
    const sourceDurationMs = segments.length
        ? segments.reduce((sum, s) => sum + (s.endMs - s.startMs), 0)
        : getClipDuration(clip)
    return sourceDurationMs / (clip.playbackRate || 1)
}

// Maps a position within the "effective" (segments-removed, speed-adjusted) clip timeline back
// to the original source time (ms, relative to the full un-trimmed source media). effectiveMs is
// *timeline* time; multiplying by playbackRate first converts it to *source* time before the
// (speed-agnostic) segment-skipping math runs.
export const mapEffectiveMsToSourceMs = (clip, effectiveMs) => {
    const rate = clip.playbackRate || 1
    const sourceMs = effectiveMs * rate
    const segments = getEnabledSegments(clip)
    if (!segments.length) return clip.trimStartMs + sourceMs

    let acc = 0
    for (const seg of segments) {
        const segDuration = seg.endMs - seg.startMs
        if (sourceMs <= acc + segDuration) {
            return clip.trimStartMs + seg.startMs + (sourceMs - acc)
        }
        acc += segDuration
    }
    const last = segments[segments.length - 1]
    return clip.trimStartMs + last.endMs
}

// Inverse of mapEffectiveMsToSourceMs: given a position in the *source* video (ms, relative to
// trimStart), returns the corresponding position on the effective (segments-removed, speed-
// adjusted) timeline. Returns null only when that position falls inside a disabled (deleted/
// silence-removed) segment that still has more enabled content *after* it (signals "skip
// forward"). Once playback has moved past every segment - the normal, common case of simply
// reaching the end of the clip - this returns the clip's total effective duration instead, so
// the caller's "did we reach the end" / "should we cut to the next clip" checks actually get a
// chance to run.
export const mapSourceMsToEffectiveMs = (clip, sourceLocalMs) => {
    const rate = clip.playbackRate || 1
    const segments = getEnabledSegments(clip)
    if (!segments.length) return sourceLocalMs / rate

    let acc = 0
    for (const seg of segments) {
        if (sourceLocalMs < seg.startMs) return null // inside a gap, more content still ahead
        if (sourceLocalMs < seg.endMs) return (acc + (sourceLocalMs - seg.startMs)) / rate
        acc += seg.endMs - seg.startMs
    }
    return acc / rate // past every segment - the clip has simply reached its natural end
}

// During native playback, once we drift into a disabled segment this returns the source-time
// (ms, relative to trimStart) to jump to next - the start of the next enabled segment, or null
// if there isn't one (end of clip reached).
export const findNextEnabledSegmentStart = (clip, sourceLocalMs) => {
    const segments = getEnabledSegments(clip)
    const next = segments.find(seg => seg.startMs >= sourceLocalMs)
    return next ? next.startMs : null
}

// Lays out clips of the media track back-to-back using their effective (post-segment-removal)
// duration. Call after any trim/segment/reorder/add/remove change.
export const recomputeTimelineLayout = (timeline) => {
    const mediaTrack = getMediaTrack(timeline)
    if (!mediaTrack) return timeline
    let cursor = 0
    mediaTrack.clips.forEach((clip) => {
        const duration = getEffectiveClipDuration(clip)
        clip.timelineStartMs = cursor
        clip.timelineEndMs = cursor + duration
        cursor += duration
    })
    return timeline
}

export const computeTimelineDurationMs = (timeline) => {
    const mediaTrack = getMediaTrack(timeline)
    if (!mediaTrack || !mediaTrack.clips.length) return 0
    return Math.max(...mediaTrack.clips.map(clip => clip.timelineEndMs))
}

// index === undefined appends at the end (the common case - adding via the timeline's own "+"
// button) - any other value inserts there instead (a clip's own hover-revealed "insert after me"
// control), clamped to a valid position so an out-of-range index never silently drops the clip.
export const addClipToTrack = (timeline, trackId, clip, index) => {
    const track = timeline.tracks.find(t => t.id === trackId)
    if (!track || !track.clips) return timeline
    const at = index === undefined || index === null ? track.clips.length : Math.max(0, Math.min(index, track.clips.length))
    track.clips.splice(at, 0, clip)
    recomputeTimelineLayout(timeline)
    return timeline
}

export const removeClipFromTrack = (timeline, trackId, clipId) => {
    const track = timeline.tracks.find(t => t.id === trackId)
    if (!track || !track.clips) return timeline
    track.clips = track.clips.filter(c => c.id !== clipId)
    recomputeTimelineLayout(timeline)
    return timeline
}

// A split too close to either edge would leave a sliver clip that's fiddly to see/select/trim
// further, and at the exact edge wouldn't produce two clips at all.
const MIN_SPLIT_REMAINDER_MS = 200

// Divides one media clip into two independent clips at `atTimelineMs` (a point on the overall
// timeline, not the clip's own local time) - each keeping the same source file, transform/crop
// and per-clip settings, but with its own trim range so the two halves can be trimmed, reordered,
// deleted or have a transition inserted between them independently from then on. Works generically
// whether or not the clip already has internal deleted segments (silence removal, manual portion
// delete) by operating in source-time via the existing effective<->source mapping helpers.
export const splitMediaClip = (timeline, clipId, atTimelineMs) => {
    const mediaTrack = getMediaTrack(timeline)
    if (!mediaTrack) return timeline
    const index = mediaTrack.clips.findIndex(c => c.id === clipId)
    if (index === -1) return timeline
    const clip = mediaTrack.clips[index]

    const atEffectiveMs = atTimelineMs - clip.timelineStartMs
    const totalEffectiveMs = getEffectiveClipDuration(clip)
    if (atEffectiveMs < MIN_SPLIT_REMAINDER_MS || atEffectiveMs > totalEffectiveMs - MIN_SPLIT_REMAINDER_MS) {
        return timeline
    }

    const sourceSplitMs = mapEffectiveMsToSourceMs(clip, atEffectiveMs)
    // Segments are stored relative to trimStartMs (segment 0 === trimStartMs) - re-expressed here
    // in that same local space so they can be sliced at the split point below.
    const localSplitMs = sourceSplitMs - clip.trimStartMs

    const firstSegments = []
    const secondSegments = []
    clip.segments.forEach((seg) => {
        if (seg.endMs <= localSplitMs) {
            firstSegments.push({ ...seg })
        } else if (seg.startMs >= localSplitMs) {
            secondSegments.push({ ...seg, startMs: seg.startMs - localSplitMs, endMs: seg.endMs - localSplitMs })
        } else {
            // This segment straddles the cut - both halves keep a (shorter) copy of it.
            firstSegments.push({ ...seg, endMs: localSplitMs })
            secondSegments.push({ ...seg, startMs: 0, endMs: seg.endMs - localSplitMs })
        }
    })

    const firstClip = deepClone(clip)
    firstClip.trimEndMs = sourceSplitMs
    firstClip.segments = firstSegments
    firstClip.silenceRanges = (clip.silenceRanges || []).filter(r => r.startMs < sourceSplitMs)
    // The two halves are now adjacent - any transition the original clip had into what follows
    // now belongs to the second half; the first half gets a clean cut with no transition of its
    // own into its new neighbor, avoiding a confusing "transition into itself" default.
    firstClip.transitionOut = { type: 'none', durationMs: 0, easing: 'linear', easingPoints: [0, 0, 1, 1] }

    const secondClip = deepClone(clip)
    secondClip.id = generateId('clip')
    secondClip.trimStartMs = sourceSplitMs
    secondClip.segments = secondSegments
    secondClip.silenceRanges = (clip.silenceRanges || []).filter(r => r.endMs > sourceSplitMs)
    secondClip.transitionIn = { type: 'none', durationMs: 0, easing: 'linear', easingPoints: [0, 0, 1, 1] }

    if (clip.sourceType === CLIP_SOURCE_TYPES.IMAGE) {
        firstClip.imageDurationMs = firstClip.trimEndMs - firstClip.trimStartMs
        secondClip.imageDurationMs = secondClip.trimEndMs - secondClip.trimStartMs
    }

    mediaTrack.clips.splice(index, 1, firstClip, secondClip)
    recomputeTimelineLayout(timeline)
    return timeline
}

// What a detection pass (or an already-reviewed/edited preview) resolves to as the clip's actual
// segments - see MediaEditor.vue's silence-preview flow (initSilencePreview/
// validateSilencePreview), which is the only place this is committed to a real clip. An empty
// array is a legitimate result (re-tuning sensitivity/minimum duration live can land on "nothing
// qualifies anymore") and must still resolve to one fully-enabled segment.
export const computeSegmentsFromSilenceRanges = (clip, silenceRanges = []) => {
    const totalMs = clip.trimEndMs - clip.trimStartMs
    if (!silenceRanges.length) return [{ startMs: 0, endMs: totalMs, enabled: true }]

    const boundaries = new Set([0, totalMs])
    silenceRanges.forEach(({ startMs, endMs }) => {
        const localStart = Math.max(0, startMs - clip.trimStartMs)
        const localEnd = Math.min(totalMs, endMs - clip.trimStartMs)
        if (localEnd > localStart) {
            boundaries.add(localStart)
            boundaries.add(localEnd)
        }
    })

    const sorted = [...boundaries].sort((a, b) => a - b)
    const segments = []
    for (let i = 0; i < sorted.length - 1; i++) {
        const startMs = sorted[i]
        const endMs = sorted[i + 1]
        const isSilent = silenceRanges.some(r => {
            const localStart = Math.max(0, r.startMs - clip.trimStartMs)
            const localEnd = r.endMs - clip.trimStartMs
            return startMs >= localStart && endMs <= localEnd
        })
        segments.push({ startMs, endMs, enabled: !isSilent })
    }
    return segments
}

export const addOverlayToTrack = (timeline, overlay) => {
    const track = getOverlayTrack(timeline)
    if (!track) return timeline
    track.overlays.push(overlay)
    return timeline
}

export const removeOverlayFromTrack = (timeline, overlayId) => {
    const track = getOverlayTrack(timeline)
    if (!track) return timeline
    track.overlays = track.overlays.filter(o => o.id !== overlayId)
    return timeline
}

// The overlays array order *is* the stacking/z order (last = frontmost, see OverlayCanvas.vue's
// enforceStackingOrder) - "avancer"/"reculer"/premier-arrière plan just move an overlay's own
// position within that array instead of tracking a separate numeric z-index to keep in sync.
export const reorderOverlayInTrack = (timeline, overlayId, direction) => {
    const track = getOverlayTrack(timeline)
    if (!track) return timeline
    const index = track.overlays.findIndex(o => o.id === overlayId)
    if (index === -1) return timeline

    const [item] = track.overlays.splice(index, 1)
    if (direction === 'front') track.overlays.push(item)
    else if (direction === 'back') track.overlays.unshift(item)
    else if (direction === 'forward') track.overlays.splice(Math.min(track.overlays.length, index + 1), 0, item)
    else if (direction === 'backward') track.overlays.splice(Math.max(0, index - 1), 0, item)
    else track.overlays.splice(index, 0, item) // unknown direction - put it back where it was

    return timeline
}

export const getActiveClipAtMs = (timeline, globalMs) => {
    const mediaTrack = getMediaTrack(timeline)
    if (!mediaTrack) return null
    return mediaTrack.clips.find(c => globalMs >= c.timelineStartMs && globalMs < c.timelineEndMs)
        || mediaTrack.clips[mediaTrack.clips.length - 1]
        || null
}

export const cloneTimeline = (timeline) => deepClone(timeline)
